import { useEffect, useState } from 'react';
import { MinusIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/components/Toast';
import { toYmd, parseLocalDate } from '@/lib/localDate';
import type { SignupSlot } from '@flock-types/signupSheets.types';
import type { SlotUpdates } from '@/services/signupSheets.service';

interface Props {
  slot: SignupSlot;
  updateSlot: (slotId: string, updates: SlotUpdates) => Promise<void>;
  onDeleteSlot: () => void;
  isMutating: boolean;
  onClose: () => void;
}

/** Edit a slot's label, date, and capacity (port of the Expo edit-slot modal). */
export default function EditSlotModal({ slot, updateSlot, onDeleteSlot, isMutating, onClose }: Props) {
  const { showToast } = useToast();
  const [label, setLabel] = useState(slot.label || '');
  const [date, setDate] = useState(() =>
    slot.slot_date ? toYmd(parseLocalDate(slot.slot_date)) : ''
  );
  const [capacity, setCapacity] = useState(slot.capacity);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async () => {
    try {
      await updateSlot(slot.id, {
        label: label.trim() || null,
        // Plain YYYY-MM-DD from the input, or null to clear the date
        slot_date: date || null,
        capacity,
      });
      onClose();
    } catch {
      showToast('Failed to update slot.', 'error');
    }
  };

  const inputClass =
    'w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-flock-navy-900">Edit Slot</h2>
          <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">
              Description
            </label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={255}
              placeholder="e.g., Paper plates (x50)"
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={inputClass}
            />
            {date && (
              <button
                onClick={() => setDate('')}
                className="mt-1 text-sm text-red-500 hover:text-red-600"
              >
                Remove date
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-flock-navy-700">Spots available</span>
            <div className="flex items-center rounded-lg border border-flock-gray-300 bg-flock-gray-100">
              <button
                onClick={() => setCapacity((c) => Math.max(1, c - 1))}
                className="px-3 py-2 text-flock-navy-600 hover:text-flock-navy-800"
                title="Fewer spots"
              >
                <MinusIcon className="h-4 w-4" />
              </button>
              <span className="min-w-[2rem] text-center text-sm font-medium text-flock-navy-800">
                {capacity}
              </span>
              <button
                onClick={() => setCapacity((c) => Math.min(100, c + 1))}
                className="px-3 py-2 text-flock-navy-600 hover:text-flock-navy-800"
                title="More spots"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-5 flex gap-3">
          <button
            onClick={onClose}
            className="min-h-touch flex-1 rounded-lg bg-flock-gray-200 px-4 py-2.5 text-sm font-medium text-flock-navy-700 transition hover:bg-flock-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isMutating}
            className="min-h-touch flex-1 rounded-lg bg-flock-navy-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-flock-navy-700 disabled:opacity-50"
          >
            {isMutating ? 'Saving…' : 'Save'}
          </button>
        </div>

        <button
          onClick={() => {
            onClose();
            onDeleteSlot();
          }}
          className="mt-3 w-full py-2 text-sm font-medium text-red-500 transition hover:text-red-600"
        >
          Delete Slot
        </button>
      </div>
    </div>
  );
}
